import { SkinnedOwnerStageElement } from "./skinned-owner-stage.js";
import { escapeHtml } from "../util/escape-html.js";
import nodeMarkUrl from "../skin/brand/node-mark.jpg?url";
const QUERY = "payments.history.v1";
const BALANCE_QUERY = "wallet.balance.v1";
export const WALLET_STAGE_TAG = "node-wallet-stage";
export const WALLET_QUERY = QUERY;
export const WALLET_BALANCE_QUERY = BALANCE_QUERY;
/**
 * Owner stage: wallet — re-clothed to the original skin's DOM skeleton (epic #1507 fidelity,
 * stage 2/5). Ported from `SKIN/stages/default/wallet/view/view.js`, verified real via
 * explicit-file grep against the vendored `client/ui/src/skin/stages-css/wallet.css` +
 * `client/ui/src/skin/css/legacy-numbered.css` (the hero/nfc/sys-card/channel/block rules live
 * there, not in `wallet.css`). This is a DOM-template pass only — no new query wiring.
 *
 * Real bindings kept exactly as before:
 * - `.wh-balance` / the Recent Activity `.wh-tag` freshness readout: `wallet.balance.v1`
 *   (`#startBalance`/`#applyBalance`, a second self-contained subscription independent of the
 *   base class's single-query lifecycle — see the class doc below).
 * - `.activity-list` `.act-row`s: `payments.history.v1` (this stage's primary `query`).
 *
 * Everything else in the original mockup (NFC card faces, on-chain/Lightning node status
 * cards, channel topology graph, block/mining visualization) has no backing kernel query (no
 * card, channel, block-height, mempool, or fee query exists yet — that's #1539's job). Per the
 * data policy this renders the ORIGINAL DOM shape with `"—"` placeholders instead of the
 * original's fabricated demo numbers (53,247 sats / "ACINQ" / "OpenNode" / block 841,205 /
 * 24 sat/vB / etc.) — never invented figures.
 *
 * Structural deviations from the original, each called out where it happens below:
 * - `.wh-actions` gains the original's third `.wh-act--swap` button (the pre-existing markup
 *   here had only Send/Receive) — all three actions stay `disabled` (read-only in this
 *   release; send/receive/swap refuse offline regardless).
 * - The pre-existing non-original freshness eyebrow (`<p class="ui-eyebrow"
 *   data-wallet-freshness>`) that used to sit inside `.wallet-hero` is gone; freshness now
 *   reads out of the Recent Activity `.wcard`'s `.wh-tag` (`data-wallet-freshness` moved onto
 *   it) — that's the original's own `24h` label slot, so freshness state (`Loading`/`Synced`/
 *   `Stale`/`Unavailable`) reuses that existing chrome instead of adding new chrome.
 * - `.chan-viz`'s SVG is a minimal center `YOU` node + a handful of unlabeled peer dots/lines,
 *   not the original's 50-line demo graph (7 named peers — `ACINQ`/`OpenNode`/`WoS`/`Strike`/
 *   `Zeus`/`Phoenix`/`LNBig` — with fabricated per-peer traffic). Node names are themselves
 *   fabricated data, so they're dropped; the graph shape (center hub + spokes) is real DOM/CSS
 *   structure, so that's kept.
 * - `.blk-stats` has 3 `.blk-stat`s (Mempool / Tx pending / Avg fee) vs. the original's 2 —
 *   all three read `"—"`, so the extra slot isn't inventing a number, just reserving the grid
 *   shape for when #1539 wires mempool/fee data.
 * - Mining `.wcard-hd`'s `h3`/`.wh-tag` drop the original's fabricated `Block 841,205` /
 *   `~10 min avg` and read plain `Mining` / `—`, matching the Channels card's `Channels` / `—`
 *   treatment.
 * - `.chan-legend` and the `.autopilot-inline` toggle (both original chrome next to the channel
 *   graph) aren't ported: the legend labels traffic that doesn't exist yet, and the autopilot
 *   switch has no backing control — porting either would either mislabel the placeholder graph
 *   or offer a toggle that does nothing, worse than omitting them.
 *
 * The wallet balance and the payment history are two separate kernel queries — this stage's
 * primary `query` (the base class's single-query lifecycle) is `payments.history.v1`; the
 * balance is a second, self-contained subscription (`#startBalance`/`#applyBalance`) that only
 * ever touches its own `[data-wallet-balance]`/`[data-wallet-freshness]` subtree, re-applied
 * after every `renderView` re-render. This keeps `SkinnedOwnerStageElement`'s
 * `renderView(root, rows)` contract unchanged for every other stage.
 */
export class WalletStageElement extends SkinnedOwnerStageElement {
    query = QUERY;
    emptyMessage = "No wallet activity yet.";
    #balance;
    #balanceClient;
    #balanceGeneration = 0;
    #balanceUnsubscribe;
    configure(options) {
        super.configure(options);
        this.#balanceClient = options.client;
        if (this.isConnected)
            this.#startBalance();
    }
    connectedCallback() {
        super.connectedCallback();
        this.#startBalance();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#balanceGeneration += 1;
        this.#balanceUnsubscribe?.();
        this.#balanceUnsubscribe = undefined;
    }
    renderView(root, rows) {
        const history = rows[0] ?? {};
        const items = Array.isArray(history.items) ? history.items : [];
        const activityBody = items.length > 0
            ? items.map((item) => renderActRow(item)).join("")
            : `<p class="ui-eyebrow">No payments yet</p>`;
        root.innerHTML = `
      <div class="wallet-page" data-wallet-page>
        <div class="wallet-hero">
          <div>
            <div class="wh-label">Balance</div>
            <div class="wh-balance" data-wallet-balance>&mdash;</div>
            <div class="wh-usd">&asymp; &mdash; USD</div>
          </div>
          <div class="wh-actions">
            <button class="wh-act primary" type="button" disabled title="Read-only in this release">
              <svg viewBox="0 0 15 15" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7.5 L12 7.5 M9 4.5 L12 7.5 L9 10.5"/></svg>
              Send
            </button>
            <button class="wh-act secondary" type="button" disabled title="Read-only in this release">
              <svg viewBox="0 0 15 15" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 7.5 L3 7.5 M6 4.5 L3 7.5 L6 10.5"/></svg>
              Receive
            </button>
            <button class="wh-act secondary wh-act--swap" type="button" disabled title="Read-only in this release">
              <svg viewBox="0 0 15 15" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 4.5 L12 4.5 M9.5 2.5 L12 4.5 L9.5 6.5 M12 10.5 L3 10.5 M5.5 8.5 L3 10.5 L5.5 12.5"/></svg>
              Swap
            </button>
          </div>
        </div>

        <div class="nfc-section">
          <div class="nfc-section-hd">
            <h3>My <em>cards.</em></h3>
            <span class="nfc-section-tag">&mdash; cards</span>
          </div>
          <div class="nfc-row">
            ${renderNfcCard("nfc-spend-a")}
            ${renderNfcCard("nfc-spend-b")}
            ${renderNfcCard("nfc-backup nfc-black")}
          </div>
        </div>

        <div class="sys-pair">
          <div class="sys-card bitcoin">
            <div class="sys-hd">
              <div class="sys-ico">
                <svg viewBox="0 0 16 16" fill="currentColor"><path d="M11.2 7.3c.2-1.2-.7-1.9-2-2.3l.4-1.7-1-.2-.4 1.6c-.3-.1-.5-.1-.8-.2l.4-1.6-1-.2-.4 1.7-.6-.1-1.4-.3-.3 1.1s.7.2.7.2c.4.1.5.4.4.6L5 8.8c.1 0 .2 0 .2.1l-.9 2.8c-.1.1-.2.3-.5.2l-.7-.2-.5 1.2 1.3.3c.3.1.5.1.7.2l-.4 1.7 1 .2.4-1.7.8.2-.4 1.7 1 .2.4-1.7c1.7.3 3 .2 3.5-1.3.5-1.2 0-1.9-.9-2.4.6-.2 1.1-.6 1.3-1.5zM9.4 11.2c-.3 1.2-2.3.6-3 .4l.6-2.3c.6.2 2.7.5 2.4 1.9zm.3-3.8c-.3 1.1-2 .6-2.6.4l.5-2.1c.6.2 2.3.5 2.1 1.7z"/></svg>
              </div>
              <div class="sys-name-block">
                <div class="sys-name">Bitcoin Node</div>
                <div class="sys-running-word">&mdash;</div>
              </div>
              <span class="sys-live"><span class="ld"></span>Waiting</span>
            </div>
            <div class="sys-metrics">
              <div class="sys-metric"><div class="sm-v">&mdash;</div><div class="sm-l">Block height</div></div>
              <div class="sys-metric"><div class="sm-v">&mdash;</div><div class="sm-l">Synced</div></div>
              <div class="sys-metric"><div class="sm-v">&mdash;</div><div class="sm-l">Mempool</div></div>
            </div>
          </div>
          <div class="sys-card lightning">
            <div class="sys-hd">
              <div class="sys-ico">
                <svg viewBox="0 0 16 16" fill="currentColor" stroke="currentColor" stroke-width="0.5" stroke-linejoin="round"><path d="M9 1 L3 8.5 H7 L6 15 L12 7.5 H8 Z"/></svg>
              </div>
              <div class="sys-name-block">
                <div class="sys-name">Lightning Node</div>
                <div class="sys-running-word">&mdash;</div>
              </div>
              <span class="sys-live"><span class="ld"></span>Waiting</span>
            </div>
            <div class="sys-metrics">
              <div class="sys-metric"><div class="sm-v">&mdash;</div><div class="sm-l">Channels</div></div>
              <div class="sys-metric"><div class="sm-v">&mdash;</div><div class="sm-l">Peers</div></div>
              <div class="sys-metric"><div class="sm-v">&mdash;</div><div class="sm-l">Uptime</div></div>
            </div>
          </div>
        </div>

        <div class="wallet-grid">
          <div class="wcard channels-card">
            <div class="wcard-hd">
              <h3>Channels</h3>
              <span class="wh-tag">&mdash;</span>
            </div>
            <div class="chan-big">&mdash;</div>
            <div class="cap-bars">
              <div class="cap-bar-row">
                <div class="cap-hd">
                  <span class="cap-label"><span class="cap-dot in"></span>Inbound</span>
                  <span class="cap-val">&mdash;</span>
                </div>
                <div class="cap-track"><div class="cap-fill in" style="width:0%"></div></div>
              </div>
              <div class="cap-bar-row">
                <div class="cap-hd">
                  <span class="cap-label"><span class="cap-dot out"></span>Outbound</span>
                  <span class="cap-val">&mdash;</span>
                </div>
                <div class="cap-track"><div class="cap-fill out" style="width:0%"></div></div>
              </div>
            </div>
            <div class="chan-viz">
              <svg viewBox="0 0 500 250" xmlns="http://www.w3.org/2000/svg">
                <line x1="250" y1="125" x2="90"  y2="40"  class="chan-line"/>
                <line x1="250" y1="125" x2="420" y2="40"  class="chan-line"/>
                <line x1="250" y1="125" x2="70"  y2="125" class="chan-line"/>
                <line x1="250" y1="125" x2="430" y2="125" class="chan-line"/>
                <line x1="250" y1="125" x2="130" y2="210" class="chan-line idle"/>
                <line x1="250" y1="125" x2="370" y2="210" class="chan-line idle"/>
                <circle cx="90"  cy="40"  r="8" class="chan-node"/>
                <circle cx="420" cy="40"  r="8" class="chan-node"/>
                <circle cx="70"  cy="125" r="8" class="chan-node"/>
                <circle cx="430" cy="125" r="8" class="chan-node"/>
                <circle cx="130" cy="210" r="6" class="chan-node" style="opacity:0.35;"/>
                <circle cx="370" cy="210" r="6" class="chan-node" style="opacity:0.35;"/>
                <circle cx="250" cy="125" r="14" class="chan-node center"/>
                <text x="250" y="159" class="node-label center" text-anchor="middle">YOU</text>
              </svg>
            </div>
            <div class="chan-timeline">
              <div class="chan-tl-hd">
                <div class="chan-tl-label">Last 24 hours</div>
                <div class="chan-tl-count">&mdash;</div>
              </div>
              <div class="chan-tl-track">
                <div class="chan-tl-bar">
                  <div class="chan-tl-fill" style="width:0%"></div>
                  <div class="chan-tl-playhead" style="left:0%"></div>
                </div>
                <div class="chan-tl-labels"><span>24h ago</span><span>now</span></div>
              </div>
            </div>
          </div>

          <div class="wcard block-card">
            <div class="wcard-hd">
              <h3>Mining</h3>
              <span class="wh-tag">&mdash;</span>
            </div>
            <div class="blk-square-wrap">
              <div class="blk-square">
                <div class="blk-grid"></div>
                <div class="blk-center-overlay">
                  <div class="blk-time">&mdash;</div>
                  <div class="blk-lbl">Since last block</div>
                </div>
              </div>
              <div class="blk-counter-row">
                <span class="blk-count-label">Pending payments</span>
                <span class="blk-count-val">&mdash;</span>
              </div>
            </div>
            <div class="fee-bar-block">
              <div class="fee-bar-hd">
                <span class="fee-bar-label">Fees right now</span>
                <span class="fee-bar-val">&mdash;<span class="fb-u">sat/vB</span></span>
              </div>
              <div class="fee-bar-track">
                <div class="fee-bar-avg-mark"></div>
                <div class="fee-bar-fill" style="width:0%"></div>
              </div>
              <div class="fee-bar-foot">
                <span class="fb-low">Low</span>
                <span class="fb-avg">Avg</span>
                <span class="fb-high">High</span>
              </div>
            </div>
            <div class="blk-stats">
              <div class="blk-stat"><div class="bs-label">Mempool</div><div class="bs-value">&mdash;</div></div>
              <div class="blk-stat"><div class="bs-label">Tx pending</div><div class="bs-value">&mdash;</div></div>
              <div class="blk-stat"><div class="bs-label">Avg fee</div><div class="bs-value">&mdash;</div></div>
            </div>
          </div>

          <div class="wcard">
            <div class="wcard-hd">
              <h3>Recent Activity</h3>
              <span class="wh-tag" data-wallet-freshness>Loading</span>
            </div>
            <div class="activity-list">${activityBody}</div>
          </div>
        </div>
      </div>
    `;
        this.#applyBalance();
    }
    #startBalance() {
        this.#balanceUnsubscribe?.();
        this.#balanceUnsubscribe = undefined;
        const client = this.#balanceClient;
        if (client === undefined)
            return;
        const generation = ++this.#balanceGeneration;
        this.#balanceUnsubscribe = client.liveQuery(BALANCE_QUERY, {}, (diff) => {
            if (generation !== this.#balanceGeneration)
                return;
            const next = diff.updated[0] ?? diff.added[0];
            if (next !== undefined) {
                this.#balance = next;
                this.#applyBalance();
            }
        });
        void client.query(BALANCE_QUERY, {})
            .then((rows) => {
            if (generation !== this.#balanceGeneration)
                return;
            this.#balance = rows[0];
            this.#applyBalance();
        })
            .catch(() => {
            // Offline/error: leave the "—" placeholder rendered by renderView() in place.
        });
    }
    #applyBalance() {
        const balanceEl = this.querySelector("[data-wallet-balance]");
        const freshnessEl = this.querySelector("[data-wallet-freshness]");
        if (balanceEl === null || freshnessEl === null)
            return;
        const balance = this.#balance;
        if (balance === undefined) {
            balanceEl.textContent = "—";
            freshnessEl.textContent = "Loading";
            return;
        }
        const balanceMsat = numberField(balance.balanceMsat);
        balanceEl.innerHTML = balanceMsat === undefined
            ? "—"
            : `${formatSats(balanceMsat)}<span class="unit">sats</span>`;
        freshnessEl.textContent = freshnessLabel(stringField(balance.freshness));
    }
}
export function registerWalletStageElement() {
    if (!customElements.get(WALLET_STAGE_TAG)) {
        customElements.define(WALLET_STAGE_TAG, WalletStageElement);
    }
}
function renderNfcCard(faceClass) {
    return `
            <div class="nfc-card-wrap">
              <div class="nfc-card ${faceClass}">
                <div class="nfc-label">Card slot</div>
                <img class="nfc-logo" src="${nodeMarkUrl}" alt="Node">
                <div class="nfc-chip"></div>
              </div>
              <div class="nfc-info">
                <div class="nfc-name">&mdash;</div>
                <div class="nfc-bal">&mdash;</div>
              </div>
            </div>`;
}
function renderActRow(item) {
    const direction = stringField(item.direction);
    const incoming = direction === "incoming";
    const amountMsat = numberField(item.amountMsat) ?? 0;
    const status = stringField(item.status) ?? "pending";
    const createdAt = stringField(item.createdAt);
    const title = incoming ? "Payment received" : "Payment sent";
    const sign = incoming ? "+" : "–";
    return `
              <div class="act-row ${incoming ? "in" : "out"}">
                <div class="act-icon">${actIconGlyph(incoming)}</div>
                <div class="act-main">
                  <div class="act-title">${escapeHtml(title)}</div>
                  <div class="act-sub">${escapeHtml(formatRelative(createdAt))} · ${escapeHtml(status)}</div>
                </div>
                <div class="act-amt">${sign}${formatSats(amountMsat)} s</div>
              </div>`;
}
function actIconGlyph(incoming) {
    return incoming
        ? '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 12 L7 3 M3.5 7 L7 3.5 L10.5 7"/></svg>'
        : '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 3 L7 12 M3.5 8 L7 11.5 L10.5 8"/></svg>';
}
function freshnessLabel(freshness) {
    switch (freshness) {
        case "fresh": return "Synced";
        case "stale": return "Stale";
        case "unavailable": return "Unavailable";
        default: return "Loading";
    }
}
function formatSats(amountMsat) {
    return Math.round(amountMsat / 1000).toLocaleString();
}
function formatRelative(value) {
    if (value === undefined)
        return "Unknown time";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime()))
        return "Unknown time";
    const diffMs = Date.now() - parsed.getTime();
    const diffMinutes = Math.round(diffMs / 60_000);
    if (diffMinutes < 1)
        return "Just now";
    if (diffMinutes < 60)
        return `${diffMinutes}m ago`;
    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24)
        return `${diffHours}h ago`;
    return parsed.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
function stringField(value) {
    return typeof value === "string" && value.length > 0 ? value : undefined;
}
function numberField(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}
//# sourceMappingURL=wallet-stage.js.map